import { useForm, Controller } from 'react-hook-form'
import { useQuery } from '@apollo/client'
import { useState, useMemo, useEffect, useRef } from 'react'
import Select, { SingleValue } from 'react-select'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react'
import {
  searchBridgePayers,
  getBridgePayer as getBridgePayerQuery,
} from './queries.graphql'
import useDebounce from 'hooks/use-debounce'
import useDynamicMaxHeight from 'hooks/use-dynamic-max-height'
import { INITIAL_SERVICE_TYPE_ID, STATES } from 'constants/bridge'
import styles from './styles.module.css'
import useCurrentUser from 'hooks/use-current-user'
import cn from 'classnames'
import DiagnosisSelect, {
  SelectOption,
} from 'components/survey/question-types/custom/diagnosis-select'
import Spinner from 'components/shared/spinner'
import { useSurveyStore } from 'stores/survey-store'
import { useEligibilityStore } from 'stores/eligibility-store'
import { stringifyDate } from 'utilities/dates'
import { STEPS } from 'components/sign-up/insurance-coverage-flow/constants'

interface FormData {
  firstName: string
  lastName: string
  dateOfBirth: string
  sex?: string
  state: string
  insuranceCompany: string
  payerId: string
  diagnosis: string[]
  memberId?: string
  skipMemberId?: boolean
}

const STATE_OPTIONS: SelectOption[] = STATES.map(state => ({
  value: state.value,
  label: state.label,
}))

interface CoverageFormProps {
  onMenuOpen?: () => void
  hasErrors?: boolean
  showMemberIdSkip?: boolean
  checkEligibility?: (options: {
    variables: {
      input: {
        clientId: number
        payerId: string
        location: { state: string }
        person: {
          firstName: string
          lastName: string
          dateOfBirth: string
        }
        serviceTypeId: number | string
        memberId?: string
        skipMembershipId?: boolean
        clinicalInfo: { diagnoses: string[] }
      }
    }
  }) => Promise<{ data?: { checkEligibility: any } } | void>
  isLoading?: boolean
  client?: any
  useEligibilityStore?: boolean // If true, use eligibilityStore instead of surveyStore
}

export const CoverageForm = ({
  onMenuOpen,
  hasErrors = false,
  showMemberIdSkip = true,
  checkEligibility,
  isLoading = false,
  client,
  useEligibilityStore: useEligibilityStoreProp = false,
}: CoverageFormProps) => {
  // Use either eligibilityStore or surveyStore based on prop
  const surveyStore = useSurveyStore()
  const eligibilityStore = useEligibilityStore()

  const store = useEligibilityStoreProp ? eligibilityStore : surveyStore

  const eligibilityInputParams = store.eligibilityInputParams
  const selectedPayer = store.selectedPayer
  const setSelectedPayer = store.setSelectedPayer
  const policyMetadata = store.policyMetadata
  const setEligibilityStatus = store.setEligibilityStatus
  const setEligibilityData = store.setEligibilityData
  const setPolicyMetadata = store.setPolicyMetadata
  const setPolicyRetryCount = store.setPolicyRetryCount
  const policyRetryCount = store.policyRetryCount
  const setLatestEligibilityParamsFromDB =
    store.setLatestEligibilityParamsFromDB
  const setEligibilityInputParams = store.setEligibilityInputParams
  const [isTyping, setIsTyping] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [skipMemberId, setSkipMemberId] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const justSelectedRef = useRef<boolean>(false)
  const dropdownMaxHeight = useDynamicMaxHeight(200)
  const [isMemberIdError, setIsMemberIdError] = useState(
    policyMetadata?.errors?.some((error: any) => error.code === 'MEMBER_ID')
  )
  const currentUserPayload = useCurrentUser()
  const user = currentUserPayload?.data?.me?.user

  const debouncedSearch = useDebounce((value: string) => {
    setSearchTerm(value)
    setIsTyping(false)
  }, 300)

  const { data: payerData, loading: payersLoading } = useQuery(
    searchBridgePayers,
    {
      variables: { searchTerm: searchTerm || '' },
      skip: !searchTerm || searchTerm.length < 2,
    }
  )

  const firstName = eligibilityInputParams?.firstName || user?.firstName
  const lastName = eligibilityInputParams?.lastName || user?.lastName
  const payerId = eligibilityInputParams?.payerId
  const memberId = eligibilityInputParams?.memberId
  const dateOfBirth = eligibilityInputParams?.dateOfBirth
    ? new Date(eligibilityInputParams?.dateOfBirth).toISOString()
    : policyMetadata?.person?.dateOfBirth
    ? new Date(policyMetadata?.person?.dateOfBirth).toISOString()
    : ''
  const diagnoses = eligibilityInputParams?.diagnoses || []
  const state = eligibilityInputParams?.state

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      state: state || '',
      diagnosis: diagnoses,
      payerId: payerId || '',
      dateOfBirth: dateOfBirth || '',
      insuranceCompany: selectedPayer?.label || selectedPayer?.name || '',
      memberId: memberId || '',
      firstName: firstName || '',
      lastName: lastName || '',
    },
  })

  // Update form values when eligibilityInputParams are set
  useEffect(() => {
    if (!eligibilityInputParams) return

    // Update firstName
    if (eligibilityInputParams.firstName) {
      setValue('firstName', eligibilityInputParams.firstName)
    }

    // Update lastName
    if (eligibilityInputParams.lastName) {
      setValue('lastName', eligibilityInputParams.lastName)
    }

    // Update dateOfBirth
    if (eligibilityInputParams.dateOfBirth) {
      try {
        const dateValue = new Date(eligibilityInputParams.dateOfBirth)
          .toISOString()
          .split('T')[0]
        setValue('dateOfBirth', dateValue)
      } catch (e) {
        // If date parsing fails, try using it as-is
        setValue('dateOfBirth', eligibilityInputParams.dateOfBirth)
      }
    }

    // Update state
    if (eligibilityInputParams.state) {
      setValue('state', eligibilityInputParams.state)
    }

    // Update diagnoses
    if (
      eligibilityInputParams.diagnoses &&
      eligibilityInputParams.diagnoses.length > 0
    ) {
      setValue('diagnosis', eligibilityInputParams.diagnoses)
    }

    // Update payerId
    if (eligibilityInputParams.payerId) {
      setValue('payerId', eligibilityInputParams.payerId)
    }

    // Update memberId
    if (eligibilityInputParams.memberId) {
      setValue('memberId', eligibilityInputParams.memberId)
    }

    // Update skipMemberId
    if (eligibilityInputParams.skipMembershipId !== null) {
      setSkipMemberId(eligibilityInputParams.skipMembershipId)
    }
  }, [eligibilityInputParams, setValue, setSkipMemberId])

  // Fetch payer details when payerId is available
  useQuery(getBridgePayerQuery, {
    variables: { bridgePayerId: eligibilityInputParams?.payerId },
    skip: !eligibilityInputParams?.payerId || selectedPayer,
    onCompleted: (data: any) => {
      setValue('insuranceCompany', data.bridgePayer.name)
      setValue('payerId', data.bridgePayer.id)
      setSelectedPayer(data.bridgePayer)
    },
  })

  const onSubmit = async (data: FormData) => {
    // Update eligibilityInputParams with new data from form
    setEligibilityInputParams({
      payerId: data.payerId || null,
      memberId: data.memberId || null,
      skipMembershipId: data.skipMemberId || null,
      state: data.state || null,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
      dateOfBirth: data.dateOfBirth || null,
      diagnoses: data.diagnosis || [],
    })

    const response = await checkEligibility?.({
      variables: {
        input: {
          clientId: client?.id,
          payerId: data.payerId,
          location: {
            state: data.state,
          },
          person: {
            firstName: data.firstName,
            lastName: data.lastName,
            dateOfBirth: data.dateOfBirth
              ? stringifyDate(data.dateOfBirth)
              : '',
          },
          serviceTypeId: INITIAL_SERVICE_TYPE_ID,
          memberId: data.memberId || undefined,
          skipMembershipId: data.skipMemberId || undefined,
          clinicalInfo: {
            diagnoses: data.diagnosis,
          },
          retryCount: policyRetryCount != null ? policyRetryCount + 1 : 0,
        } as any,
      },
    })

    // Update Zustand store with response
    if (response?.data?.checkEligibility) {
      const eligibilityResponse = response.data.checkEligibility
      const isErrorWithRetry =
        eligibilityResponse.currentStep === STEPS.POLICY_INVALID &&
        eligibilityResponse.retryCount >= 1

      const status = isErrorWithRetry
        ? STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE
        : eligibilityResponse.currentStep

      setEligibilityStatus(status)
      setLatestEligibilityParamsFromDB(eligibilityResponse?.eligibilityParams)
      setEligibilityData(eligibilityResponse.serviceEligibilityMetadata)
      setPolicyMetadata(eligibilityResponse.policyMetadata)
      setPolicyRetryCount(eligibilityResponse.retryCount)
    }
  }

  const payers = useMemo(() => payerData?.searchBridgePayers || [], [payerData])
  const payersLength = useMemo(() => payers.length, [payers])

  useEffect(() => {
    if (payersLength > 0) {
      onMenuOpen?.()
    }
  }, [onMenuOpen, payersLength])

  const getSelectValue = (value: string, options: SelectOption[]) => {
    return options.find(option => option.value === value) || null
  }

  return (
    <div className={styles.container}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn(styles.form, {
          [styles.errorInput]: hasErrors,
        })}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            First Name<span className={styles.required}>*</span>
          </label>
          <input
            {...register('firstName', {
              required: 'First name is required',
            })}
            className={styles.input}
          />
          {errors.firstName && (
            <span className={styles.error}>{errors.firstName.message}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Last Name<span className={styles.required}>*</span>
          </label>
          <input
            {...register('lastName', {
              required: 'Last name is required',
            })}
            className={styles.input}
          />
          {errors.lastName && (
            <span className={styles.error}>{errors.lastName.message}</span>
          )}
        </div>

        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              State<span className={styles.required}>*</span>
            </label>
            <Controller
              name="state"
              control={control}
              rules={{ required: 'State is required' }}
              render={({ field }) => (
                <Select<SelectOption>
                  {...field}
                  value={getSelectValue(field.value, STATE_OPTIONS)}
                  options={STATE_OPTIONS}
                  className={styles.select}
                  classNamePrefix="select"
                  placeholder="Search..."
                  isClearable
                  onChange={(option: SingleValue<SelectOption>) => {
                    field.onChange(option ? option.value : '')
                  }}
                />
              )}
            />
            {errors.state && (
              <span className={styles.error}>{errors.state.message}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Date of Birth<span className={styles.required}>*</span>
            </label>
            <input
              type="date"
              {...register('dateOfBirth', {
                required: 'Date of birth is required',
              })}
              className={cn(styles.input, styles.dateInput, {
                [styles.hasValue]: !!watch('dateOfBirth'),
              })}
            />
            {errors.dateOfBirth && (
              <span className={styles.error}>{errors.dateOfBirth.message}</span>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Health Insurance Provider<span className={styles.required}>*</span>
          </label>
          <Controller
            name="insuranceCompany"
            control={control}
            rules={{ required: 'Insurance provider is required' }}
            render={({ field }) => {
              const payerOptions = payers.map((p: any) => ({
                value: p.id,
                label: p.name,
                memberId: p.memberId,
              }))

              const currentValue = field.value
                ? { value: field.value, label: field.value, memberId: 'false' }
                : null

              const handleInputChange = (value: string) => {
                setIsTyping(true)
                setSearchTerm(value)
                // Clear selection when user starts typing different value
                if (field.value && value !== field.value) {
                  field.onChange('')
                  setValue('payerId', '')
                  setSelectedPayer(null)
                }
                debouncedSearch(value)
              }

              const handleSelect = (option: any) => {
                // Ignore null calls that happen immediately after a valid selection
                if (option === null && justSelectedRef.current) {
                  justSelectedRef.current = false
                  return
                }

                if (option) {
                  setSearchTerm(option.label)
                  field.onChange(option.label)
                  const payer = payers.find((p: any) => p.id === option.value)
                  if (payer) {
                    setValue('payerId', payer.id)
                    setSelectedPayer(payer)
                    setIsMemberIdError(false)
                  }
                  justSelectedRef.current = true
                  setTimeout(() => {
                    justSelectedRef.current = false
                  }, 100)
                  inputRef.current?.blur()
                } else {
                  field.onChange('')
                  setValue('payerId', '')
                  setSelectedPayer(null)
                }
              }

              return (
                <div className={styles.autocompleteContainer}>
                  <Combobox
                    value={currentValue}
                    onChange={handleSelect}
                    nullable>
                    {() => (
                      <>
                        <ComboboxInput
                          ref={inputRef}
                          className={styles.comboboxInput}
                          displayValue={() => searchTerm || field.value || ''}
                          onChange={event =>
                            handleInputChange(event.target.value)
                          }
                          placeholder="Search..."
                          onFocus={() => {
                            onMenuOpen?.()
                            inputRef.current?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'center',
                            })
                          }}
                        />
                        {searchTerm && searchTerm.length >= 2 && !isTyping && (
                          <ComboboxOptions
                            className={styles.comboboxOptions}
                            style={{ maxHeight: dropdownMaxHeight }}>
                            {searchTerm.length >= 2 &&
                            !isTyping &&
                            searchTerm !== field.value ? (
                              payersLoading ? (
                                <div className={styles.noResults}>
                                  Searching...
                                </div>
                              ) : payerOptions.length > 0 ? (
                                payerOptions.map((option: any) => (
                                  <ComboboxOption
                                    key={option.value}
                                    value={option}
                                    className={({ active, selected }) =>
                                      cn(styles.comboboxOption, {
                                        [styles.comboboxOptionActive]: active,
                                        [styles.comboboxOptionSelected]:
                                          selected,
                                      })
                                    }>
                                    {option.label}
                                  </ComboboxOption>
                                ))
                              ) : (
                                <div className={styles.noResults}>
                                  No insurance companies found
                                </div>
                              )
                            ) : searchTerm.length < 2 && !isTyping ? (
                              <div className={styles.noResults}>
                                Type at least 2 characters to search
                              </div>
                            ) : null}
                          </ComboboxOptions>
                        )}
                      </>
                    )}
                  </Combobox>
                </div>
              )
            }}
          />
          {errors.insuranceCompany && (
            <span className={styles.error}>
              {errors.insuranceCompany.message}
            </span>
          )}
        </div>

        {(selectedPayer?.memberId === 'true' ||
          hasErrors ||
          isMemberIdError) && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Member ID{' '}
              {!skipMemberId &&
                (selectedPayer?.memberId === 'true' || isMemberIdError) && (
                  <span className={styles.required}>*</span>
                )}
            </label>
            <input
              {...register('memberId', {
                validate: value => {
                  const skipMemberId = watch('skipMemberId')
                  // If memberId is required and no value provided, field is required
                  if (isMemberIdError && !value && !skipMemberId) {
                    return 'Member ID is required'
                  }

                  // If memberId is 'false', field is not required
                  if (selectedPayer?.memberId === 'false') {
                    return true
                  }

                  // If memberId is 'true' and user has checked skip, field is not required
                  if (skipMemberId && selectedPayer?.memberId === 'true') {
                    return true
                  }

                  // If memberId is 'true' and no value provided, field is required
                  if (selectedPayer?.memberId === 'true' && !value) {
                    return 'Member ID is required'
                  }

                  return true
                },
              })}
              className={styles.input}
            />
            {errors.memberId && (
              <span className={styles.error}>{errors.memberId.message}</span>
            )}

            {showMemberIdSkip &&
              (selectedPayer?.memberId === 'true' || isMemberIdError) && (
                <div className={styles.checkboxContainer}>
                  <label className={styles.label}>
                    <input
                      type="checkbox"
                      checked={skipMemberId}
                      className={styles.checkbox}
                      onChange={e => {
                        const checked = e.target.checked
                        setSkipMemberId(checked)
                        setValue('skipMemberId', checked)
                        if (checked) {
                          setValue('memberId', '')
                          clearErrors('memberId')
                        }
                      }}
                    />
                    <span className={styles.checkboxText}>
                      I don't have it right now
                    </span>
                  </label>
                </div>
              )}
          </div>
        )}

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Health Conditions (If Applicable)
          </label>
          <Controller
            name="diagnosis"
            control={control}
            render={({ field }) => {
              return (
                <DiagnosisSelect
                  className={styles.select}
                  onChange={(selectedOptions: any) => {
                    const values = (selectedOptions as SelectOption[]).map(
                      option => option.value
                    )
                    field.onChange(values)
                  }}
                  value={field.value}
                  useMobileBottomSheet
                />
              )
            }}
          />
          {errors.diagnosis && (
            <span className={styles.error}>{errors.diagnosis.message}</span>
          )}
        </div>

        <button type="submit" className={styles.button} disabled={isLoading}>
          Verify Insurance Coverage
          {isLoading && <Spinner className={styles.spinner} />}
        </button>
      </form>
    </div>
  )
}
